import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useCreateValidationsList } from '../../hooks'
import { useValidationsByFeatureStore } from '../../stores'

import { AppIcons, SkeletonListTemplate, CardItem } from '@/components'
import { BaseView } from '@/components/base'

export const ValidationByFeatureValidationsList: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const feature = useValidationsByFeatureStore((s) => s.feature)

  const { validations, isLoading } = useCreateValidationsList({ feature })

  const visibleValidations = validations?.filter(
    (validation) => !!validation.isVisible,
  )

  useEffect(() => {
    if (!isLoading) {
      const everyVisibleValidationIsNotPending = visibleValidations?.every(
        ({ isPending }) => !isPending,
      )

      //IF ALL VISIBLE VALIDATIONS IS NOT PENDING, RETURN FOR PREVIOUS PAGE
      if (everyVisibleValidationIsNotPending) {
        if (window.history.length > 1) {
          navigate(-1)
        } else {
          navigate('/dashboard')
        }
      }
    }
  }, [isLoading, visibleValidations, navigate])

  if (isLoading) {
    return <SkeletonListTemplate sizeList={3} />
  }

  const handleItemPress = (validation: any) => {
    // Use navigation for proper routing instead of the validation's onPress
    if (!validation.isDisabled) {
      if (validation.iconName === 'phone') {
        navigate('/add-phone')
      } else if (validation.iconName === 'emailInline') {
        navigate('/check-your-email')
      } else if (validation.iconName === 'identity') {
        navigate('/check-your-identity')
      }
    }
  }

  return (
    <BaseView gap={14} px={12}>
      {visibleValidations
        ?.filter((validation) => validation.isVisible)
        .map((validation) => {
          const {
            id,
            iconName,
            iconBgColor,
            title,
            isPending,
            isDisabled,
            disabledMessage,
          } = validation

          return (
            <CardItem.Root
              key={id}
              disabled={isDisabled}
              onPress={() => handleItemPress(validation)}
              style={{ opacity: isDisabled ? 0.5 : 1 }}
            >
              <CardItem.AvatarGradient
                iconName={iconName}
                colors={
                  Array.isArray(iconBgColor) ? iconBgColor : [iconBgColor]
                }
                borderRadiusRatio={3}
                iconSize={24}
                size={64}
              />
              <CardItem.Content>
                <CardItem.Title title={t(title)} />

                <BaseView row align="center" gap={4}>
                  {!!isPending && <AppIcons name="clock" size={16} />}
                  <CardItem.Description
                    description={t(
                      disabledMessage
                        ? t(disabledMessage)
                        : isPending
                          ? 'pending'
                          : 'verified',
                    )}
                  />
                </BaseView>
              </CardItem.Content>

              <CardItem.RightIcon>
                <AppIcons
                  name={isPending ? 'chevronRight' : 'check'}
                  size={24}
                />
              </CardItem.RightIcon>
            </CardItem.Root>
          )
        })}
    </BaseView>
  )
}
